import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap";
import Select from "react-select";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { reloadWindowToPath } from "../../Helpers/Utils";
import { loadingFalse, loadingTrue } from "../../Reducer/loaderSlice";

const CreateProduct = () => {
    const dispatch = useDispatch();
    /* ================= HELPERS ================= */
    const isEmpty = (v) => v === "" || v === null || v === undefined;

    const isVariantFilled = (v) =>
        v.name || v.price || v.purchase_price || v.discount_type ||
        v.discount_value || v.gst_rate || v.quantity || v.low_stock_limit;

    /* ================= STATES ================= */
    const [form, setForm] = useState({
        name: "", sku: "", dealer: "", brand: "", description: "", price: "", purchase_price: "", gst_rate: "", stock: "0", unit: null, quantity: null, low_stock_limit: "", discount_type: null, discount_value: "", category: null, sub_category: null
    });

    const [variants, setVariants] = useState([{
        name: "",
        price: "",
        purchase_price: "",
        discount_type: null,
        discount_value: "",
        gst_rate: "",
        quantity: "",
        low_stock_limit: ""
    }]);

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const user = useSelector(s => s.customer.customer);

    const loading = useSelector(state => state.loading.loading);

    const discountTypes = [
        { value: "flat", label: "Flat" },
        { value: "percentage", label: "Percentage" }
    ];

    /* ================= SKU ================= */
    const generateSKU = (name) => {
        const base = name ? name.replace(/\s+/g, "").substring(0, 5).toUpperCase() : "PROD";
        return `${base}-${Math.floor(1000 + Math.random() * 9000)}`;
    };

    useEffect(() => {
        if (form.name) setForm(p => ({ ...p, sku: generateSKU(p.name) }));
    }, [form.name]);

    /* ================= LOAD CATEGORY ================= */
    useEffect(() => { loadCategories(); }, []);

    const loadCategories = async () => {
        const res = await Handler({ method: "get", url: "/categories/list.php?limit=1000" });
        if (res?.success) {
            setCategories(res.data.categories.map(c => ({ value: c.id, label: c.name })));
        }
    };

    const loadSubCategories = async (id) => {
        const res = await Handler({ method: "get", url: `/sub_categories/list.php?category_id=${id}&limit=1000` });
        setSubCategories(
            res?.success
                ? res.data.sub_categories.map(sc => ({ value: sc.id, label: sc.name }))
                : []
        );
    };

    /* ================= VARIANTS ================= */
    const updateVariant = (i, k, v) => {
        const copy = [...variants];
        copy[i][k] = v;
        setVariants(copy);
    };

    const unitOptions = [
        { value: "g", label: "Gram (g)" },
        { value: "kg", label: "Kilogram (kg)" },
        { value: "ml", label: "Millilitre (ml)" },
        { value: "ltr", label: "Litre (ltr)" },
        { value: "pcs", label: "Pieces (pcs)" },
        { value: "Standard", label: "Standard (Standard)" }
    ];


    const addVariant = () => {
        const last = variants[variants.length - 1];

        // if last variant has any value, it must be fully completed
        if (isVariantFilled(last)) {
            toast.error("Complete current variant before adding new one");
            return;
        }

        setVariants([...variants, {
            name: "",
            price: "",
            purchase_price: "",
            discount_type: null,
            discount_value: "",
            gst_rate: "",
            quantity: "",
            low_stock_limit: ""
        }]);
    };

    /* ================= SUBMIT ================= */
    /* ================= SUBMIT ================= */
    const submit = async () => {
        /* ===== MAIN PRODUCT VALIDATION ===== */
        if (!form.name.trim()) {
            toast.error("Product name required");
            return;
        } else if (!form.category) {
            toast.error("Category required");
            return;
        } else if (isEmpty(form.price)) {
            toast.error("Price required");
            return;
        } else if (isEmpty(form.purchase_price)) {
            toast.error("Purchase price required");
            return;
        } else if (Number(form.price) < Number(form.purchase_price)) {
            toast.error("Selling price cannot be less than Purchase price");
            return;
        } else if (isEmpty(form.gst_rate)) {
            toast.error("GST rate required");
            return;
        } else if (isEmpty(form.quantity)) {
            toast.error("Quantity required (0 allowed)");
            return;
        } else if (!form.sku.trim()) {
            toast.error("SKU required");
            return;
        } else if (!form.unit) {
            toast.error("Unit required");
            return;
        }

        /* ===== VARIANT VALIDATION ===== */
        for (let i = 0; i < variants.length; i++) {
            const v = variants[i];
            if (!isVariantFilled(v)) continue;

            if (!v.name) {
                toast.error(`Variant ${i + 1}: Name required`);
                return;
            } else if (isEmpty(v.price)) {
                toast.error(`Variant ${i + 1}: Price required`);
                return;
            } else if (isEmpty(v.purchase_price)) {
                toast.error(`Variant ${i + 1}: Purchase price required`);
                return;
            } else if (Number(v.price) < Number(v.purchase_price)) {
                toast.error(`Variant ${i + 1}: Selling price cannot be less than Purchase price`);
                return;
            } else if (isEmpty(v.gst_rate)) {
                toast.error(`Variant ${i + 1}: GST rate required`);
                return;
            }
        }

        dispatch(loadingTrue());

        /* ===== PAYLOAD WITH OPTIONAL CHAINING ===== */
        const payload = {
            name: form.name,
            price: Number(form.price),
            purchase_price: Number(form.purchase_price),
            // Inga dhaan machan fix: Optional chaining + fallback value
            discount_type: form.discount_type?.value || "",
            discount_value: Number(form.discount_value || 0),
            gst_rate: Number(form.gst_rate),
            outlet_id: user.outlet_id,
            category_id: form.category?.value || "",
            sub_category_id: form.sub_category?.value || "",
            dealer: form.dealer,
            brand: form.brand,
            sku: form.sku,
            size: `${form.quantity} ${form.unit?.value || ""}`,
            description: form.description,
            quantity: Number(form.stock || 0),
            low_stock_limit: Number(form.low_stock_limit || 0),
            variants: variants
                .filter(v => isVariantFilled(v))
                .map(v => ({
                    name: v.name,
                    price: Number(v.price),
                    purchase_price: Number(v.purchase_price),
                    // Variant-kum optional chaining mukkiyam
                    discount_type: v.discount_type?.value || "",
                    discount_value: Number(v.discount_value || 0),
                    gst_rate: Number(v.gst_rate),
                    quantity: Number(v.quantity || 0),
                    low_stock_limit: Number(v.low_stock_limit || 0)
                }))
        };

        const res = await Handler({
            method: "post",
            url: "/products/create.php",
            data: payload
        });

        if (res?.success) {
            toast.success("Product added successfully");
            reloadWindowToPath('/products');
            dispatch(loadingFalse());
        } else {
            dispatch(loadingFalse());
            toast.error(res?.message || "Something went wrong");
        }
    };

    /* ================= UI ================= */
    return (
        <>
            <Row className="mb-4">
                <Col><h4 className="fw-bold">Create Product</h4></Col>
            </Row>

            {/* ================= PRODUCT ================= */}
            <Card className="mb-4"><CardBody>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Product Name *</label>
                        <Input placeholder="Enter product name" value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>SKU</label>
                        <Input placeholder="Auto generated" value={form.sku} readOnly />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Dealer Name</label>
                        <Input placeholder="Dealer name" value={form.dealer}
                            onChange={e => setForm({ ...form, dealer: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>Brand </label>
                        <Input placeholder="Brand" value={form.brand}
                            onChange={e => setForm({ ...form, brand: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Price *</label>
                        <Input type="number" placeholder="Selling price" value={form.price}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, price: e.target.value });
                                }
                            }} />
                    </Col>
                    <Col md={4}>
                        <label>Purchase Price *</label>
                        <Input type="number" placeholder="Purchase price" value={form.purchase_price}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, purchase_price: e.target.value });
                                }
                            }} />
                    </Col>
                    <Col md={4}>
                        <label>GST % *</label>
                        <Input type="number" placeholder="GST %" value={form.gst_rate}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, gst_rate: e.target.value });
                                }
                            }} />
                    </Col>
                    <Col md={6}>
                        <label>Unit *</label>
                        <Select
                            options={unitOptions}
                            value={form.unit}
                            onChange={v => setForm({ ...form, unit: v })}
                        />
                    </Col>
                    <Col md={6}>
                        <label>Quantity *</label>
                        <Input type="number" placeholder="0 allowed"
                            value={form.quantity}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, quantity: e.target.value });
                                }
                            }} />
                    </Col>

                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Discount Type</label>
                        <Select options={discountTypes} value={form.discount_type}
                            onChange={v => setForm({ ...form, discount_type: v })} />
                    </Col>
                    <Col md={4}>
                        <label>Discount Value</label>
                        <Input type="number" placeholder="Discount value"
                            value={form.discount_value}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, discount_value: e.target.value });
                                }
                            }} />
                    </Col>

                    <Col md={4}>
                        <label>Stock</label>
                        <Input type="number" placeholder="0 allowed"
                            value={form.stock}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, stock: e.target.value });
                                }
                            }} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Category *</label>
                        <Select options={categories} value={form.category}
                            onChange={v => { setForm({ ...form, category: v, sub_category: null }); loadSubCategories(v.value); }} />
                    </Col>
                    <Col md={4}>
                        <label>Sub Category *</label>
                        <Select options={subCategories} value={form.sub_category}
                            onChange={v => setForm({ ...form, sub_category: v })} />
                    </Col>
                    <Col md={4}>
                        <label>Low Stock Limit</label>
                        <Input type="number" placeholder="Low stock alert"
                            value={form.low_stock_limit}
                            onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) {
                                    setForm({ ...form, low_stock_limit: e.target.value });
                                }
                            }} />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <label>Description</label>
                        <Input type="textarea" rows="3" placeholder="Product description"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })} />
                    </Col>
                </Row>

            </CardBody></Card>

            {/* ================= VARIANTS ================= */}
            <Card className="mb-4"><CardBody>

                <div className="d-flex justify-content-between mb-3">
                    <h6>Variants</h6>
                    <Button disabled={loading} color="light" onClick={addVariant}>+ Add Variant</Button>
                </div>

                {variants && variants?.map((v, i) => (
                    <div key={i} className="border border-light rounded p-3 mb-3 bg-light bg-opacity-10">

                        <Row className="g-3 mb-3">
                            <Col md={4}>
                                <label>Variant Name *</label>
                                <Input placeholder="Variant name" value={v.name}
                                    onChange={e => updateVariant(i, "name", e.target.value)} />
                            </Col>
                            <Col md={4}>
                                <label>Price *</label>
                                <Input type="number" placeholder="Selling price" value={v.price}
                                    onChange={e => updateVariant(i, "price", e.target.value)} />
                            </Col>
                            <Col md={4}>
                                <label>Purchase Price *</label>
                                <Input type="number" placeholder="Purchase price" value={v.purchase_price}
                                    onChange={e => updateVariant(i, "purchase_price", e.target.value)} />
                            </Col>
                        </Row>

                        <Row className="g-3 mb-3">
                            <Col md={4}>
                                <label>Discount Type</label>
                                <Select options={discountTypes} value={v.discount_type}
                                    onChange={val => updateVariant(i, "discount_type", val)} />
                            </Col>
                            <Col md={4}>
                                <label>Discount Value</label>
                                <Input type="number" placeholder="Discount value"
                                    value={v.discount_value}
                                    onChange={e => updateVariant(i, "discount_value", e.target.value)} />
                            </Col>
                            <Col md={4}>
                                <label>GST % *</label>
                                <Input type="number" placeholder="GST %"
                                    value={v.gst_rate}
                                    onChange={e => updateVariant(i, "gst_rate", e.target.value)} />
                            </Col>
                        </Row>

                        <Row className="g-3">
                            <Col md={6}>
                                <label>Stock</label>
                                <Input type="number" placeholder="0 allowed" value={v.quantity}
                                    onChange={e => updateVariant(i, "quantity", e.target.value)} />
                            </Col>
                            <Col md={6}>
                                <label>Low Stock Limit</label>
                                <Input type="number" placeholder="Low stock alert"
                                    value={v.low_stock_limit}
                                    onChange={e => updateVariant(i, "low_stock_limit", e.target.value)} />
                            </Col>
                        </Row>

                    </div>
                ))}

            </CardBody></Card>

            <div className="text-end">
                <Button disabled={loading} color="success" onClick={submit}>Add Product</Button>
            </div>
        </>
    );
};

export default CreateProduct;
